import React, { useCallback, useState, useEffect, ChangeEvent, useMemo } from 'react';
import { TextField, Paper, Button, Icon, IconButton, FormControl, MenuItem, ListItemIcon, Menu, Box, makeStyles, Select, Checkbox, FormControlLabel,
    FormHelperText, Theme } from '@material-ui/core';
import { TFunction } from 'i18next';
import clsx from 'clsx';
import produce, { Draft } from 'immer';
import { MonitorActionType, MonitorAction, TicketSystemType, CreateTicketMonitorActionParams, CreateIssueMonitorActionParams,
    SendEmailMonitorActionParams, CreateIssueMonitorAction, SendEmailMonitorAction, CreateTicketMonitorAction, CreateIssueMonitorActionRecoveryParams,
    SendEmailMonitorActionRecoveryParams, SupportedTicketSystems, SlackMessageMonitorActionParams, SlackMessageMonitorActionRecoveryParams,
    SlackMessageMonitorAction,
    SlackSettings } from '@moderepo/mode-apis';
import { SelectInputOption, useModeFormStyle, FontIcon, capitalizeAll, useDropdown, EMAIL_VALIDATION_REGEX, capitalizeFirst }
    from '@moderepo/mode-app-components';
import { useEditMonitorStyle } from './EditMonitorStyle';
import * as RCUtils from '../../../containers/robot-cloud/RCUtils';
import { MonitorSetupInputErrors, EditMonitorStepProps } from './EditMonitorInfoStep';



const useStyle = makeStyles((theme: Theme) => {
    return {
        formControlGroup: {
            '& .remove-action': {
                position : 'absolute',
                top      : 5,
                right    : 5,
                transform: 'translate3d(50%, -50%, 0)',
            },

            '& .title': {
                fontWeight   : 500,
                textAlign    : 'center',
                paddingBottom: theme.spacing(1),
            },
        },

        actionTypeMenu: {
        },

        actionTypeMenuIcon: {
            fontSize: 24,
            width   : 24,
            height  : 24,
            color   : theme.palette.primary.main,
        },

        createActionSettings: {
            border : '1px solid #cccccc',
            padding: theme.spacing(1),
        },

        settingOption: {
            display                     : 'flex',
            flexDirection               : 'column',
            alignItems                  : 'center',
            justifyContent              : 'flex-start',
            marginBottom                : theme.spacing(2),
            [theme.breakpoints.up('sm')]: {
                flexDirection: 'row',
            },
            '& .form-control': {
                marginBottom: 0,
            },
        },

        settingOptionIcon: {
            width      : 25,
            height     : 25,
            fontSize   : 25,
            marginRight: theme.spacing(1),
            color      : theme.palette.primary.main,
        },

        settingOptionLabel: {
            fontWeight  : 500,
            textAlign   : 'center',
            fontSize    : 'larger',
            paddingRight: theme.spacing(2),
            whiteSpace  : 'nowrap',
        },

        settingOptionInput: {
            minWidth: '10em',
        },

        stepContentActionsContainer: {
            marginTop: theme.spacing(2),
        },

        recoveryOptionConfirmation: {
            display                     : 'flex',
            flexDirection               : 'column',
            justifyContent              : 'center',
            alignItems                  : 'center',
            [theme.breakpoints.up('md')]: {
                flexDirection : 'row',
                alignItems    : 'center',
                justifyContent: 'flex-start',
            },

            '&.send-email, &.post-slack-message': {
                marginBottom: theme.spacing(1),
            },
        },

        recoveryOptionConfirmationQuestion: {
            marginLeft: 0,
        },
    };
});


/**
 * Custom hook to create an Array of Action types used for Monitor Action input e.g. SendEmail, CreateIssue, etc...
 * @param trans
 */
const useCreateActionTypeOptions = (trans: TFunction): readonly SelectInputOption<MonitorActionType>[] => {
    const [options, setOptions] = useState<readonly SelectInputOption<MonitorActionType>[]>([]);

    useEffect(() => {
        setOptions(Object.values(MonitorActionType).map((actionType: MonitorActionType): SelectInputOption<MonitorActionType> => {
            return {
                label: capitalizeAll(trans(RCUtils.getMonitorActionTypeName(actionType))),
                value: actionType,
                icon : trans(RCUtils.getMonitorActionTypeIcon(actionType)),
            };
        }));
    }, [trans]);

    return options;
};



/**
 * Custom hook to create an Array of Ticket System type used for Create Ticket Monitor action
 * @param trans
 */
const useCreateTicketSystemTypeOptions = (trans: TFunction): readonly SelectInputOption<TicketSystemType>[] => {
    const [options, setOptions] = useState<readonly SelectInputOption<TicketSystemType>[]>([]);

    useEffect(() => {
        setOptions(Object.values(TicketSystemType).map((type: TicketSystemType): SelectInputOption<TicketSystemType> => {
            return {
                label: type,
                value: type,
            };
        }));
    }, [trans]);

    return options;
};



/**
 * Custom hook to create an Array of Slack Channel Ids type used for Slack Message Monitor action
 * @param trans
 */
const useCreateSlackChannelIdOptions = (
    slackChannels: SlackSettings['channels'] | undefined,
    trans: TFunction,
): readonly SelectInputOption<string>[] => {

    const options = useMemo(() => {
        if (slackChannels && slackChannels.length > 0) {
            return slackChannels.map((channel) => {
                return {
                    label: channel.name,
                    value: channel.channelId,
                };
            });
        }
        return [{
            label   : capitalizeAll(trans('label.no_options')),
            value   : '',
            disabled: true,
        }];
    }, [slackChannels, trans]);

    return options;
};


interface ActionInput {
    readonly parameters: any;
    readonly recoveryParameters?: any | undefined;
}

interface CreateIssueActionInput extends ActionInput {
    readonly parameters: {
        readonly description?: string | undefined;
    }
    readonly recoveryParameters?: {
        readonly comment?: string | undefined;
    } | undefined;
}


interface SendEmailActionInput extends ActionInput {
    readonly parameters: {
        readonly email?: string | undefined;
        readonly subject?: string | undefined;
        readonly body?: string | undefined;
    };
    readonly recoveryParameters?: {
        readonly subject?: string | undefined;
        readonly body?: string | undefined;
    } | undefined;
}

interface CreateTicketActionInput extends ActionInput {
    readonly parameters: {
        readonly systemType?: TicketSystemType;
    };
    readonly recoveryParameters?: { } | undefined;
}

/**
 * Input interface for Slack action settings
 */
interface SlackMessageActionInput extends ActionInput {
    readonly parameters: {
        readonly channelId?: string | undefined;
        readonly message?: string | undefined;
        readonly iconEmoji?: string | undefined;
        readonly username?: string | undefined;
    };
    readonly recoveryParameters?: {
        readonly message?: string | undefined;
        readonly iconEmoji?: string | undefined;
        readonly username?: string | undefined;
    } | undefined;
}


/**
 * Find an action from the list of actions of type "type" and then convert it to the corresponding ActionInput
 * @param actions - The set of actions
 * @param type - The type of action to look for
 */
const findAndConvertActionToInput = (
    actions: MonitorAction[] | undefined, type: MonitorActionType,
): ActionInput | undefined => {
    if (actions) {
        // find the action of the specified type
        const action = actions.find((s: MonitorAction) => {
            return s.type === type;
        });

        if (action) {
            switch (action.type) {
                case MonitorActionType.CREATE_ISSUE:
                    return {
                        parameters: {
                            ...(action.parameters as CreateIssueMonitorActionParams),
                        },
                        recoveryParameters: action.recoveryParameters ? {
                            ...(action.recoveryParameters as CreateIssueMonitorActionRecoveryParams),
                        } : undefined,
                    } as CreateIssueActionInput;
                case MonitorActionType.SEND_EMAIL:
                    return {
                        parameters: {
                            ...(action.parameters as SendEmailMonitorActionParams),
                        },
                        recoveryParameters: action.recoveryParameters ? {
                            ...(action.recoveryParameters as SendEmailMonitorActionRecoveryParams),
                        } : undefined,
                    } as SendEmailActionInput;
                case MonitorActionType.CREATE_TICKET:
                    // For CreateTicketActionInput, we need to convert the parameters.parameters Object to string
                    return {
                        parameters: {
                            systemType: (action.parameters as CreateTicketMonitorActionParams).systemType,
                            parameters: JSON.stringify((action.parameters as CreateTicketMonitorActionParams).parameters, undefined, 4),
                        },
                        recoveryParameters: action.recoveryParameters ? {
                        } : undefined,
                    } as CreateTicketActionInput;
                case MonitorActionType.SLACK_MESSAGE:
                    return {
                        parameters: {
                            ...(action.parameters as SlackMessageMonitorActionParams),
                        },
                        recoveryParameters: action.recoveryParameters ? {
                            ...(action.recoveryParameters as SlackMessageMonitorActionRecoveryParams),
                        } : undefined,
                    } as SlackMessageActionInput;
                default:
                    return {
                        parameters: {},
                    };
            }
        }
    }

    return undefined;
};



/**
 * Errors fields for Monitor Actions setup step
 */
interface MonitorActionsInputErrors extends MonitorSetupInputErrors {
    noAction?: string | undefined;
    createIssue?: {
        description?: string | undefined;
    } | undefined;
    sendEmail?: {
        email?: string | undefined;
        subject?: string | undefined;
        body?: string | undefined;
        recoverySubject?: string | undefined;
        recoveryBody?: string | undefined;
    } | undefined;
    createTicket?: {
        systemType?: string | undefined;
    } | undefined;
    slackMessage?: {
        channelId?: string | undefined;
        message?: string | undefined;
        iconEmoji?: string | undefined;
        username?: string | undefined;
        recoverMessage?: string | undefined;
        recoverIconEmoji?: string | undefined;
        recoverUsername?: string | undefined;
    } | undefined;
}



interface EditMonitorActionsStepProps extends EditMonitorStepProps {
    readonly supportedTicketSystems?: SupportedTicketSystems | undefined;
    readonly slackChannels?: SlackSettings['channels'] | undefined;
    readonly actions?: MonitorAction[] | undefined;
    readonly onStepCompleted: (actions: MonitorAction[])=> void;
}


/**
 * This is the HTML for the Setup Monitor Actions step content
 * @param props
 */
export const EditMonitorActionsStep = (props: EditMonitorActionsStepProps) => {

    const { trans } = props;
    const formClasses = useModeFormStyle();
    const globalClasses = useEditMonitorStyle();
    const classes = useStyle();

    // Dropdown menu for the Create Monitor Type menu
    const [monitorActionMenuAnchorEl, isMonitorActionMenuOpened, onMonitorActionAnchorElClicked, onMonitorActionMenuClosed] = useDropdown();


    // These hold the current value of the user input. We will initialize these with the values from props
    const [createIssueActionInput, setCreateIssueActionInput] = useState<CreateIssueActionInput | undefined>(
        findAndConvertActionToInput(props.actions, MonitorActionType.CREATE_ISSUE),
    );
    const [sendEmailActionInput, setSendEmailActionInput] = useState<SendEmailActionInput | undefined>(
        findAndConvertActionToInput(props.actions, MonitorActionType.SEND_EMAIL),
    );
    const [createTicketActionInput, setCreateTicketActionInput] = useState<CreateTicketActionInput | undefined>(
        findAndConvertActionToInput(props.actions, MonitorActionType.CREATE_TICKET),
    );
    const [slackMessageActionInput, setSlackMessageActionInput] = useState<SlackMessageActionInput | undefined>(
        findAndConvertActionToInput(props.actions, MonitorActionType.SLACK_MESSAGE),
    );

    const [inputErrors, setInputErrors] = useState<MonitorActionsInputErrors>({ });


    // Create a map of the inputs and their setters defined above using MonitorActionType as IDs so that we can lookup
    // inputs/setters by MonitorActionType
    const inputsAndSetters: {
        [monitorActionType: string]: {
            inputs: ActionInput | undefined,
            setter: React.Dispatch<React.SetStateAction<ActionInput | undefined>>
        }
    } = useMemo(() => {
        return {
            [MonitorActionType.CREATE_ISSUE]: {
                inputs: createIssueActionInput, setter: setCreateIssueActionInput,
            },
            [MonitorActionType.SEND_EMAIL]: {
                inputs: sendEmailActionInput, setter: setSendEmailActionInput,
            },
            [MonitorActionType.CREATE_TICKET]: {
                inputs: createTicketActionInput, setter: setCreateTicketActionInput,
            },
            [MonitorActionType.SLACK_MESSAGE]: {
                inputs: slackMessageActionInput, setter: setSlackMessageActionInput,
            },
        };
    }, [createIssueActionInput, sendEmailActionInput, createTicketActionInput, slackMessageActionInput]);



    // These are the set of options used for the Dropdown/Select input
    const monitorActionTypeOptions = useCreateActionTypeOptions(trans);
    const ticketSystemTypeOptions = useCreateTicketSystemTypeOptions(trans);
    const slackChannelIdOptions = useCreateSlackChannelIdOptions(props.slackChannels, trans);


    // Check to see which Ticket system the project support so that we can enable/disable Create ticket option accordingly
    const supportSalesforce: boolean = useMemo(() => {
        return Boolean(props.supportedTicketSystems && props.supportedTicketSystems.salesForceSettings
        && props.supportedTicketSystems.salesForceSettings.clientId && props.supportedTicketSystems.salesForceSettings.clientSecret
        && props.supportedTicketSystems.salesForceSettings.username && props.supportedTicketSystems.salesForceSettings.password);
    }, [props.supportedTicketSystems]);

    const supportAsana: boolean = useMemo(() => {
        return Boolean(props.supportedTicketSystems && props.supportedTicketSystems.asanaSettings);
    }, [props.supportedTicketSystems]);

    const supportJira: boolean = useMemo(() => {
        return Boolean(props.supportedTicketSystems && props.supportedTicketSystems.jiraSettings);
    }, [props.supportedTicketSystems]);

    const supportTicketSystems = supportSalesforce || supportAsana || supportJira;
    const hasConfiguredSlackChannels = props.slackChannels && props.slackChannels.length > 0;


    /**
     * Validate Monitor Info form and dispatch step completed if success
     */
    const validateInputs = useCallback((dispatchOnComplete: boolean = true, dispatchOnError: boolean = true): boolean => {

        const newInputErrors: MonitorActionsInputErrors = { };

        // Validate all input that are NOT undefined
        if (createIssueActionInput !== undefined) {
            if (!createIssueActionInput.parameters.description || !createIssueActionInput.parameters.description.trim()) {
                newInputErrors.createIssue = {
                    ...newInputErrors.createIssue,
                    description: 'error.input.monitor_action_create_issue.description.empty',
                };
            }
        }

        if (sendEmailActionInput !== undefined) {
            if (!sendEmailActionInput.parameters.email || !sendEmailActionInput.parameters.email.trim()) {
                newInputErrors.sendEmail = {
                    ...newInputErrors.sendEmail,
                    email: 'error.input.monitor_action_send_email.email.empty',
                };
            } else if (sendEmailActionInput.parameters.email && !EMAIL_VALIDATION_REGEX.test(sendEmailActionInput.parameters.email)) {
                newInputErrors.sendEmail = {
                    ...newInputErrors.sendEmail,
                    email: 'error.input.monitor_action_send_email.email.invalid',
                };
            }
            /*
            if (!sendEmailActionInput.parameters.subject || !sendEmailActionInput.parameters.subject.trim()) {
                newInputErrors.sendEmail = {
                    ...newInputErrors.sendEmail,
                    subject: 'error.input.monitor_action_send_email.subject.empty',
                };
            }
            */
            if (!sendEmailActionInput.parameters.body || !sendEmailActionInput.parameters.body.trim()) {
                newInputErrors.sendEmail = {
                    ...newInputErrors.sendEmail,
                    body: 'error.input.monitor_action_send_email.body.empty',
                };
            }
            if (sendEmailActionInput.recoveryParameters) {
                // If the user chose to send another email when Monitor is recover, need to make sure the user provided a subject and body
                // for the recover email
                /*
                if (!sendEmailActionInput.recoveryParameters.subject || !sendEmailActionInput.recoveryParameters.subject.trim()) {
                    newInputErrors.sendEmail = {
                        ...newInputErrors.sendEmail,
                        recoverySubject: 'error.input.monitor_action_send_email.recovery_email_subject.empty',
                    };
                }
                */
                if (!sendEmailActionInput.recoveryParameters.body || !sendEmailActionInput.recoveryParameters.body.trim()) {
                    newInputErrors.sendEmail = {
                        ...newInputErrors.sendEmail,
                        recoveryBody: 'error.input.monitor_action_send_email.recovery_email_body.empty',
                    };
                }
            }
        }

        if (createTicketActionInput !== undefined) {
            if (!createTicketActionInput.parameters.systemType) {
                newInputErrors.createTicket = {
                    ...newInputErrors.createTicket,
                    systemType: 'error.input.monitor_action_create_ticket.system_type.empty',
                };
            }
        }


        // Validate Slack action
        if (slackMessageActionInput !== undefined) {
            if (!slackMessageActionInput.parameters.channelId || !slackMessageActionInput.parameters.channelId.trim()) {
                newInputErrors.slackMessage = {
                    ...newInputErrors.slackMessage,
                    channelId: 'error.input.monitor_action_slack_message.channel_id.empty',
                };
            }
            if (!slackMessageActionInput.parameters.message || !slackMessageActionInput.parameters.message.trim()) {
                newInputErrors.slackMessage = {
                    ...newInputErrors.slackMessage,
                    message: 'error.input.monitor_action_slack_message.message.empty',
                };
            }
            if (slackMessageActionInput.recoveryParameters) {
                // If the user chose to send another email when Monitor is recover, need to make sure the user provided a subject and body
                // for the recover email
                if (!slackMessageActionInput.recoveryParameters.message || !slackMessageActionInput.recoveryParameters.message.trim()) {
                    newInputErrors.slackMessage = {
                        ...newInputErrors.slackMessage,
                        recoverMessage: 'error.input.monitor_action_slack_message.recovery_message.empty',
                    };
                }
            }
        }


        // If there are NO error, it could mean the user didn't add any action so make sure there is at least 1 action
        if (!newInputErrors.createIssue && !newInputErrors.createTicket && !newInputErrors.sendEmail && !newInputErrors.slackMessage
            && createIssueActionInput === undefined && sendEmailActionInput === undefined && createTicketActionInput === undefined
            && slackMessageActionInput === undefined) {
            newInputErrors.noAction = 'error.input.monitor_action.empty';
        }


        // If there is AT LEAST 1 field in the newInputError, this Step is considered to contain errors.
        const hasErrors = Object.keys(newInputErrors).length > 0;


        // Update the local inputErrors
        setInputErrors(newInputErrors);


        // If 'dispatchOnError' is specified, dispatch the error or null to the container
        if (dispatchOnError) {
            props.onStepError(hasErrors);
        }


        if (!hasErrors && dispatchOnComplete) {
            // Only dispatch onComplete if dispatchOnComplete is true. Sometime we just want to validate the input but not submit the form.

            // Convert the input back into MonitorAction
            const actions: MonitorAction[] = [];

            // Add CreateIssueMonitorAction to the result if there are input for it
            if (createIssueActionInput && createIssueActionInput.parameters.description) {
                actions.push({
                    type      : MonitorActionType.CREATE_ISSUE,
                    parameters: {
                        description: createIssueActionInput.parameters.description.trim(),
                    },
                    recoveryParameters: createIssueActionInput.recoveryParameters,
                } as CreateIssueMonitorAction);
            }

            // Add SendEmailMonitorAction to the result if there are input for it
            if (sendEmailActionInput && sendEmailActionInput.parameters.email && sendEmailActionInput.parameters.body) {
                actions.push({
                    type      : MonitorActionType.SEND_EMAIL,
                    parameters: {
                        email  : sendEmailActionInput.parameters.email.trim(),
                        subject: '', // TODO - Enable this later when Subject is supported sendEmailActionInput.subject.trim(),
                        body   : sendEmailActionInput.parameters.body.trim(),
                    },
                    recoveryParameters: sendEmailActionInput.recoveryParameters,
                } as SendEmailMonitorAction);
            }

            // Add CreateTicketMonitorAction to the result if there are input for it
            if (createTicketActionInput && createTicketActionInput.parameters.systemType) {
                actions.push({
                    type      : MonitorActionType.CREATE_TICKET,
                    parameters: {
                        systemType: createTicketActionInput.parameters.systemType,
                        parameters: {},
                    },
                    recoveryParameters: createTicketActionInput.recoveryParameters,
                } as CreateTicketMonitorAction);
            }

            // Add SlackMessageMonitorAction to the result if there are input for it
            if (slackMessageActionInput && slackMessageActionInput.parameters.channelId && slackMessageActionInput.parameters.message) {
                actions.push({
                    type      : MonitorActionType.SLACK_MESSAGE,
                    parameters: {
                        channelId: slackMessageActionInput.parameters.channelId,
                        message  : slackMessageActionInput.parameters.message,
                        iconEmoji: slackMessageActionInput.parameters.iconEmoji,
                        username : slackMessageActionInput.parameters.username,
                    },
                    recoveryParameters: slackMessageActionInput.recoveryParameters,
                } as SlackMessageMonitorAction);
            }

            props.onStepCompleted(actions);
        }

        return !hasErrors;
    }, [createIssueActionInput, sendEmailActionInput, createTicketActionInput, props, slackMessageActionInput]);



    /**
     * Add/Enable an MonitorAction of type 'type'
     */
    const addMonitorAction = useCallback((type: MonitorActionType) => {
        // To add/enable an Monitor action, we just need to set the inputs for that Monitor type to {} and initialize it with props.actions data
        inputsAndSetters[type].setter(
            findAndConvertActionToInput(props.actions, type) || { parameters: {} },
        );
    }, [props, inputsAndSetters]);



    /**
     * Create the HTML for the Create Issue action settings. For create Issue Action, the agent need to provide the Issue severity and a
     * description for the issue
     */
    const renderCreateIssueMonitorAction = useCallback((
        actionInputs: CreateIssueActionInput | undefined,
        inputsSetter: React.Dispatch<React.SetStateAction<CreateIssueActionInput | undefined>>,
    ) => {

        if (!actionInputs) {
            return <></>;
        }

        return (
            <Paper
                elevation={1}
                className={clsx(globalClasses.formControlGroup, classes.formControlGroup)}
            >
                <IconButton
                    className="remove-action"
                    color="secondary"
                    onClick={() => {
                        // set the input to undefined
                        inputsSetter(undefined);
                    }}
                >
                    <Icon>cancel</Icon>
                </IconButton>

                <div className={classes.settingOption}>
                    <FontIcon
                        className={classes.settingOptionIcon}
                        iconName={trans(RCUtils.getMonitorActionTypeIcon(MonitorActionType.CREATE_ISSUE))}
                    />
                    <div className={classes.settingOptionLabel}>{capitalizeFirst(trans('monitor_action_setting.create_issue.title'))}</div>
                </div>
                <TextField
                    className={clsx(formClasses.formInputField, globalClasses.formControl, 'form-control')}
                    variant="outlined"
                    type="text"
                    required
                    fullWidth
                    autoFocus
                    label={capitalizeAll(trans('label.description'))}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    multiline
                    minRows={2}
                    maxRows={5}
                    value={actionInputs.parameters.description || ''}
                    error={Boolean(inputErrors.createIssue?.description)}
                    helperText={inputErrors.createIssue?.description ? trans(inputErrors.createIssue.description) : undefined}
                    onChange={(event: React.FormEvent) => {
                        // Input changed so clear the error and update the state
                        props.onStepError(false);

                        setInputErrors(produce(inputErrors, (draft: Draft<MonitorActionsInputErrors>) => {
                            if (draft.createIssue) {
                                delete draft.createIssue.description;
                            }
                        }));

                        setCreateIssueActionInput(produce(actionInputs, (draft: Draft<CreateIssueActionInput>) => {
                            draft.parameters.description = (event.currentTarget as HTMLInputElement).value;
                        }));
                    }}
                />
                <div className={classes.recoveryOptionConfirmation}>
                    <FormControlLabel
                        className={clsx(classes.recoveryOptionConfirmationQuestion)}
                        control={(
                            <Checkbox
                                value={actionInputs.recoveryParameters !== undefined}
                                checked={actionInputs.recoveryParameters !== undefined}
                                color="primary"
                                onChange={(event: any) => {
                                    // Input changed so clear the error and update the state
                                    props.onStepError(false);

                                    setCreateIssueActionInput(produce(actionInputs, (draft: Draft<CreateIssueActionInput>) => {
                                        draft.recoveryParameters = event.target.checked ? {} : undefined;
                                    }));
                                }}
                            />
                        )}
                        label={capitalizeFirst(trans('monitor_action_setting.create_issue.recovery_action_confirmation'))}
                        labelPlacement="end"
                    />
                </div>
            </Paper>
        );
    }, [globalClasses, classes, formClasses, trans, props, inputErrors]);



    /**
     * Create the HTML for the Send Email action settings. For Send Email Action, the agent need to provide the email address, subject, and body
     */
    const renderSendEmailMonitorAction = useCallback((
        actionInputs: SendEmailActionInput | undefined,
        inputsSetter: React.Dispatch<React.SetStateAction<SendEmailActionInput | undefined>>,
    ) => {

        if (!actionInputs) {
            return <></>;
        }

        return (
            <Paper
                elevation={1}
                className={clsx(globalClasses.formControlGroup, classes.formControlGroup)}
            >
                <IconButton
                    className="remove-action"
                    color="secondary"
                    onClick={() => {
                        inputsSetter(undefined);
                    }}
                >
                    <Icon>cancel</Icon>
                </IconButton>

                <div className={classes.settingOption}>
                    <FontIcon
                        className={classes.settingOptionIcon}
                        iconName={trans(RCUtils.getMonitorActionTypeIcon(MonitorActionType.SEND_EMAIL))}
                    />
                    <div className={classes.settingOptionLabel}>{capitalizeFirst(trans('monitor_action_setting.send_email.title'))}</div>
                    <TextField
                        className={clsx(formClasses.formInputField, globalClasses.formControl, 'form-control', classes.settingOptionInput)}
                        variant="standard"
                        type="email"
                        fullWidth
                        autoFocus
                        placeholder={capitalizeAll(trans('label.email'))}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        value={actionInputs.parameters.email || ''}
                        error={Boolean(inputErrors.sendEmail?.email)}
                        helperText={inputErrors.sendEmail?.email ? trans(inputErrors.sendEmail.email) : undefined}
                        onChange={(event: React.FormEvent) => {
                            // Input changed so clear the error and update the state
                            props.onStepError(false);

                            setInputErrors(produce(inputErrors, (draft: Draft<MonitorActionsInputErrors>) => {
                                if (draft.sendEmail) {
                                    delete draft.sendEmail.email;
                                }
                            }));

                            inputsSetter(produce(actionInputs, (draft: Draft<SendEmailActionInput>) => {
                                draft.parameters.email = (event.currentTarget as HTMLInputElement).value;
                            }));
                        }}
                    />
                </div>

                <TextField
                    className={clsx(formClasses.formInputField, globalClasses.formControl, 'form-control')}
                    variant="outlined"
                    type="text"
                    required
                    fullWidth
                    autoFocus
                    label={capitalizeAll(trans('label.email_body'))}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    multiline
                    minRows={3}
                    maxRows={5}
                    value={actionInputs.parameters.body || ''}
                    error={Boolean(inputErrors.sendEmail?.body)}
                    helperText={inputErrors.sendEmail?.body ? trans(inputErrors.sendEmail.body) : undefined}
                    onChange={(event: React.FormEvent) => {
                        // Input changed so clear the error and update the state
                        props.onStepError(false);

                        setInputErrors(produce(inputErrors, (draft: Draft<MonitorActionsInputErrors>) => {
                            if (draft.sendEmail) {
                                delete draft.sendEmail.body;
                            }
                        }));

                        inputsSetter(produce(actionInputs, (draft: Draft<SendEmailActionInput>) => {
                            draft.parameters.body = (event.currentTarget as HTMLInputElement).value;
                        }));
                    }}
                />

                <div className={clsx(classes.recoveryOptionConfirmation, 'send-email')}>
                    <FormControlLabel
                        className={clsx(classes.recoveryOptionConfirmationQuestion)}
                        control={(
                            <Checkbox
                                value={actionInputs.recoveryParameters !== undefined}
                                checked={actionInputs.recoveryParameters !== undefined}
                                color="primary"
                                onChange={(event: any) => {
                                    // Input changed so clear the error and update the state
                                    props.onStepError(false);

                                    setSendEmailActionInput(produce(actionInputs, (draft: Draft<SendEmailActionInput>) => {
                                        draft.recoveryParameters = event.target.checked ? {} : undefined;
                                    }));
                                }}
                            />
                        )}
                        label={capitalizeFirst(trans('monitor_action_setting.send_email.recovery_action_confirmation'))}
                        labelPlacement="end"
                    />
                </div>

                {actionInputs?.recoveryParameters !== undefined && (
                    <TextField
                        className={clsx(formClasses.formInputField, globalClasses.formControl, 'form-control')}
                        variant="outlined"
                        type="text"
                        required
                        fullWidth
                        autoFocus
                        label={capitalizeAll(trans('label.recovery_email_body'))}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        multiline
                        minRows={3}
                        maxRows={5}
                        value={actionInputs.recoveryParameters.body || ''}
                        error={Boolean(inputErrors.sendEmail?.recoveryBody)}
                        helperText={inputErrors.sendEmail?.recoveryBody ? trans(inputErrors.sendEmail.recoveryBody) : undefined}
                        onChange={(event: React.FormEvent) => {
                            // Input changed so clear the error and update the state
                            props.onStepError(false);

                            setInputErrors(produce(inputErrors, (draft: Draft<MonitorActionsInputErrors>) => {
                                if (draft.sendEmail) {
                                    delete draft.sendEmail.recoveryBody;
                                }
                            }));

                            inputsSetter(produce(actionInputs, (draft: Draft<SendEmailActionInput>) => {
                                if (!draft.recoveryParameters) {
                                    draft.recoveryParameters = {
                                        body: (event.currentTarget as HTMLInputElement).value,
                                    };
                                } else {
                                    draft.recoveryParameters.body = (event.currentTarget as HTMLInputElement).value;
                                }
                            }));
                        }}
                    />
                )}
            </Paper>
        );
    }, [globalClasses, formClasses, classes, trans, props, inputErrors]);



    /**
     * Create the HTML for the CreateTicketMonitorAction settings. For CreateTicketMonitorAction, the agent need to choose the ticket system and
     * provide the parameters to be passed to the system
     */
    const renderCreateTicketMonitorAction = useCallback((
        actionInputs: CreateTicketActionInput | undefined,
        inputsSetter: React.Dispatch<React.SetStateAction<CreateTicketActionInput | undefined>>,
    ) => {

        if (!actionInputs) {
            return <></>;
        }

        return (
            <Paper
                elevation={1}
                className={clsx(globalClasses.formControlGroup, classes.formControlGroup)}
            >
                <IconButton
                    className="remove-action"
                    color="secondary"
                    onClick={() => {
                        inputsSetter(undefined);
                    }}
                >
                    <Icon>cancel</Icon>
                </IconButton>

                <div className={classes.settingOption}>
                    <FontIcon
                        className={classes.settingOptionIcon}
                        iconName={trans(RCUtils.getMonitorActionTypeIcon(MonitorActionType.CREATE_TICKET))}
                    />
                    <div className={classes.settingOptionLabel}>{capitalizeFirst(trans('monitor_action_setting.create_ticket.title'))}</div>
                    <FormControl
                        className={clsx(globalClasses.formControl, classes.settingOptionInput, 'form-control')}
                        error={Boolean(inputErrors.createTicket?.systemType)}
                    >
                        <Select
                            value={actionInputs.parameters.systemType || ''}
                            onChange={(event: ChangeEvent<{value: unknown}>) => {
                                // Input changed so clear the error and update the state
                                props.onStepError(false);

                                setInputErrors(produce(inputErrors, (draft: Draft<MonitorActionsInputErrors>) => {
                                    if (draft.createTicket) {
                                        delete draft.createTicket.systemType;
                                    }
                                }));

                                inputsSetter(produce(actionInputs, (draft: Draft<CreateTicketActionInput>) => {
                                    draft.parameters.systemType = event.target.value as TicketSystemType;
                                }));
                            }}
                        >
                            {ticketSystemTypeOptions.map((option: SelectInputOption<TicketSystemType>) => {
                                return (
                                    <MenuItem
                                        key={option.label}
                                        value={option.value}
                                        disabled={
                                            (option.value === TicketSystemType.SALESFORCE && !supportSalesforce)
                                            || (option.value === TicketSystemType.ASANA && !supportAsana)
                                            || (option.value === TicketSystemType.JIRA && !supportJira)
                                        }
                                    >
                                        {option.label}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                        {inputErrors.createTicket?.systemType && (
                            <FormHelperText>
                                {trans(inputErrors.createTicket?.systemType)}
                            </FormHelperText>
                        )}
                    </FormControl>
                </div>

            </Paper>
        );
    }, [globalClasses, classes, trans, ticketSystemTypeOptions, props, inputErrors, supportSalesforce, supportAsana, supportJira]);



    /**
     * Create the HTML for the Slack Message action settings. For Slack Message Action, the agent need to provide the channelId and message
     */
    const renderSlackMessageMonitorAction = useCallback((
        actionInputs: SlackMessageActionInput | undefined,
        inputsSetter: React.Dispatch<React.SetStateAction<SlackMessageActionInput | undefined>>,
    ) => {

        if (!actionInputs) {
            return <></>;
        }

        return (
            <Paper
                elevation={1}
                className={clsx(globalClasses.formControlGroup, classes.formControlGroup)}
            >
                <IconButton
                    className="remove-action"
                    color="secondary"
                    onClick={() => {
                        // set the input to undefined
                        inputsSetter(undefined);
                    }}
                >
                    <Icon>cancel</Icon>
                </IconButton>

                <div className={classes.settingOption}>
                    <FontIcon
                        className={classes.settingOptionIcon}
                        iconName={trans(RCUtils.getMonitorActionTypeIcon(MonitorActionType.SLACK_MESSAGE))}
                    />
                    <div className={classes.settingOptionLabel}>{capitalizeFirst(trans('monitor_action_setting.slack_message.title'))}</div>
                    <FormControl
                        className={clsx(globalClasses.formControl, classes.settingOptionInput, 'form-control')}
                        error={Boolean(inputErrors.slackMessage?.channelId)}
                    >
                        <Select
                            value={actionInputs.parameters.channelId || ''}
                            onChange={(event: ChangeEvent<{value: unknown}>) => {
                                // Input changed so clear the error and update the state
                                props.onStepError(false);

                                setInputErrors(produce(inputErrors, (draft: Draft<MonitorActionsInputErrors>) => {
                                    if (draft.slackMessage) {
                                        delete draft.slackMessage.channelId;
                                    }
                                }));

                                inputsSetter(produce(actionInputs, (draft: Draft<SlackMessageActionInput>) => {
                                    draft.parameters.channelId = event.target.value as string;
                                }));
                            }}
                        >
                            {slackChannelIdOptions.map((option: SelectInputOption<string>) => {
                                return (
                                    <MenuItem
                                        key={option.label}
                                        value={option.value}
                                        disabled={option.disabled}
                                    >
                                        {option.label}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                        {inputErrors.slackMessage?.channelId && (
                            <FormHelperText>
                                {trans(inputErrors.slackMessage?.channelId)}
                            </FormHelperText>
                        )}
                    </FormControl>
                </div>
                <TextField
                    className={clsx(formClasses.formInputField, globalClasses.formControl, 'form-control')}
                    variant="outlined"
                    type="text"
                    required
                    fullWidth
                    autoFocus
                    label={capitalizeAll(trans('label.message'))}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    multiline
                    minRows={2}
                    maxRows={5}
                    value={actionInputs.parameters.message || ''}
                    error={Boolean(inputErrors.slackMessage?.message)}
                    helperText={inputErrors.slackMessage?.message ? trans(inputErrors.slackMessage.message)
                        : trans('input.slack_message.hint', { interpolation: { skipOnVariables: true } })}
                    onChange={(event: React.FormEvent) => {
                        // Input changed so clear the error and update the state
                        props.onStepError(false);

                        setInputErrors(produce(inputErrors, (draft: Draft<MonitorActionsInputErrors>) => {
                            if (draft.slackMessage) {
                                delete draft.slackMessage.message;
                            }
                        }));

                        setSlackMessageActionInput(produce(actionInputs, (draft: Draft<SlackMessageActionInput>) => {
                            draft.parameters.message = (event.currentTarget as HTMLInputElement).value;
                        }));
                    }}
                />
                <div className={clsx(formClasses.formInputGroup, 'x-small')}>
                    <TextField
                        className={clsx(formClasses.formInputField, globalClasses.formControl, 'form-control')}
                        variant="outlined"
                        type="text"
                        fullWidth
                        autoFocus
                        label={capitalizeAll(trans('label.slack_icon_emoji'))}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        minRows={2}
                        maxRows={5}
                        value={actionInputs.parameters.iconEmoji || ''}
                        error={Boolean(inputErrors.slackMessage?.iconEmoji)}
                        helperText={inputErrors.slackMessage?.iconEmoji ? trans(inputErrors.slackMessage.iconEmoji) : undefined}
                        onChange={(event: React.FormEvent) => {
                            // Input changed so clear the error and update the state
                            props.onStepError(false);

                            setInputErrors(produce(inputErrors, (draft: Draft<MonitorActionsInputErrors>) => {
                                if (draft.slackMessage) {
                                    delete draft.slackMessage.iconEmoji;
                                }
                            }));

                            setSlackMessageActionInput(produce(actionInputs, (draft: Draft<SlackMessageActionInput>) => {
                                draft.parameters.iconEmoji = (event.currentTarget as HTMLInputElement).value;
                            }));
                        }}
                    />
                    <TextField
                        className={clsx(formClasses.formInputField, globalClasses.formControl, 'form-control')}
                        variant="outlined"
                        type="text"
                        fullWidth
                        autoFocus
                        label={capitalizeAll(trans('label.slack_mention_username'))}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        minRows={2}
                        maxRows={5}
                        value={actionInputs.parameters.username || ''}
                        error={Boolean(inputErrors.slackMessage?.username)}
                        helperText={inputErrors.slackMessage?.username ? trans(inputErrors.slackMessage.username) : undefined}
                        onChange={(event: React.FormEvent) => {
                            // Input changed so clear the error and update the state
                            props.onStepError(false);

                            setInputErrors(produce(inputErrors, (draft: Draft<MonitorActionsInputErrors>) => {
                                if (draft.slackMessage) {
                                    delete draft.slackMessage.username;
                                }
                            }));

                            setSlackMessageActionInput(produce(actionInputs, (draft: Draft<SlackMessageActionInput>) => {
                                draft.parameters.username = (event.currentTarget as HTMLInputElement).value;
                            }));
                        }}
                    />
                </div>
                <div className={clsx(classes.recoveryOptionConfirmation, 'post-slack-message')}>
                    <FormControlLabel
                        className={clsx(classes.recoveryOptionConfirmationQuestion)}
                        control={(
                            <Checkbox
                                value={actionInputs.recoveryParameters !== undefined}
                                checked={actionInputs.recoveryParameters !== undefined}
                                color="primary"
                                onChange={(event: any) => {
                                    // Input changed so clear the error and update the state
                                    props.onStepError(false);

                                    setSlackMessageActionInput(produce(actionInputs, (draft: Draft<SlackMessageActionInput>) => {
                                        draft.recoveryParameters = event.target.checked ? {} : undefined;
                                    }));
                                }}
                            />
                        )}
                        label={capitalizeFirst(trans('monitor_action_setting.slack_message.recovery_action_confirmation'))}
                        labelPlacement="end"
                    />
                </div>

                {actionInputs?.recoveryParameters !== undefined && (
                    <>
                        <TextField
                            className={clsx(formClasses.formInputField, globalClasses.formControl, 'form-control')}
                            variant="outlined"
                            type="text"
                            required
                            fullWidth
                            autoFocus
                            label={capitalizeAll(trans('label.recovery_slack_message'))}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            multiline
                            value={actionInputs.recoveryParameters.message || ''}
                            error={Boolean(inputErrors.slackMessage?.recoverMessage)}
                            helperText={inputErrors.slackMessage?.recoverMessage ? trans(inputErrors.slackMessage?.recoverMessage)
                                : trans('input.slack_message.hint', { interpolation: { skipOnVariables: true } })}
                            onChange={(event: React.FormEvent) => {
                                // Input changed so clear the error and update the state
                                props.onStepError(false);

                                setInputErrors(produce(inputErrors, (draft: Draft<MonitorActionsInputErrors>) => {
                                    if (draft.slackMessage) {
                                        delete draft.slackMessage.recoverMessage;
                                    }
                                }));

                                inputsSetter(produce(actionInputs, (draft: Draft<SlackMessageActionInput>) => {
                                    if (!draft.recoveryParameters) {
                                        draft.recoveryParameters = {
                                            message: (event.currentTarget as HTMLInputElement).value,
                                        };
                                    } else {
                                        draft.recoveryParameters.message = (event.currentTarget as HTMLInputElement).value;
                                    }
                                }));
                            }}
                        />
                        <div className={clsx(formClasses.formInputGroup, 'x-small')}>
                            <TextField
                                className={clsx(formClasses.formInputField, globalClasses.formControl, 'form-control')}
                                variant="outlined"
                                type="text"
                                fullWidth
                                autoFocus
                                label={capitalizeAll(trans('label.slack_icon_emoji'))}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={actionInputs.recoveryParameters.iconEmoji || ''}
                                error={Boolean(inputErrors.slackMessage?.recoverIconEmoji)}
                                helperText={inputErrors.slackMessage?.recoverIconEmoji
                                    ? trans(inputErrors.slackMessage?.recoverIconEmoji) : undefined}
                                onChange={(event: React.FormEvent) => {
                                    // Input changed so clear the error and update the state
                                    props.onStepError(false);

                                    setInputErrors(produce(inputErrors, (draft: Draft<MonitorActionsInputErrors>) => {
                                        if (draft.slackMessage) {
                                            delete draft.slackMessage.recoverIconEmoji;
                                        }
                                    }));

                                    inputsSetter(produce(actionInputs, (draft: Draft<SlackMessageActionInput>) => {
                                        if (!draft.recoveryParameters) {
                                            draft.recoveryParameters = {
                                                iconEmoji: (event.currentTarget as HTMLInputElement).value,
                                            };
                                        } else {
                                            draft.recoveryParameters.iconEmoji = (event.currentTarget as HTMLInputElement).value;
                                        }
                                    }));
                                }}
                            />
                            <TextField
                                className={clsx(formClasses.formInputField, globalClasses.formControl, 'form-control')}
                                variant="outlined"
                                type="text"
                                fullWidth
                                autoFocus
                                label={capitalizeAll(trans('label.slack_mention_username'))}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={actionInputs.recoveryParameters.username || ''}
                                error={Boolean(inputErrors.slackMessage?.recoverUsername)}
                                helperText={inputErrors.slackMessage?.recoverUsername ? trans(inputErrors.slackMessage?.recoverUsername) : undefined}
                                onChange={(event: React.FormEvent) => {
                                    // Input changed so clear the error and update the state
                                    props.onStepError(false);

                                    setInputErrors(produce(inputErrors, (draft: Draft<MonitorActionsInputErrors>) => {
                                        if (draft.slackMessage) {
                                            delete draft.slackMessage.recoverUsername;
                                        }
                                    }));

                                    inputsSetter(produce(actionInputs, (draft: Draft<SlackMessageActionInput>) => {
                                        if (!draft.recoveryParameters) {
                                            draft.recoveryParameters = {
                                                username: (event.currentTarget as HTMLInputElement).value,
                                            };
                                        } else {
                                            draft.recoveryParameters.username = (event.currentTarget as HTMLInputElement).value;
                                        }
                                    }));
                                }}
                            />
                        </div>
                    </>
                )}
            </Paper>
        );
    }, [globalClasses.formControlGroup, globalClasses.formControl, classes.formControlGroup, classes.settingOption, classes.settingOptionIcon,
        classes.settingOptionLabel, classes.settingOptionInput, classes.recoveryOptionConfirmation, classes.recoveryOptionConfirmationQuestion,
        trans, inputErrors, slackChannelIdOptions, formClasses.formInputField, formClasses.formInputGroup, props]);



    return (
        <div className={globalClasses.stepContentBody}>
            <div className={clsx(globalClasses.stepContentForm)}>
                <div className={clsx(globalClasses.stepSubtitle)}>
                    {trans('create_monitor.step_actions.subtitle')}
                    {inputErrors.noAction && (
                        <FormHelperText error>
                            {trans(inputErrors.noAction)}
                        </FormHelperText>
                    )}
                </div>


                {ticketSystemTypeOptions.length > 0 && (
                    <div>
                        {/* For each action, render the HTML for the action input fields */}
                        {renderCreateIssueMonitorAction(
                            inputsAndSetters[MonitorActionType.CREATE_ISSUE].inputs, inputsAndSetters[MonitorActionType.CREATE_ISSUE].setter,
                        )}
                        {renderSendEmailMonitorAction(
                            inputsAndSetters[MonitorActionType.SEND_EMAIL].inputs, inputsAndSetters[MonitorActionType.SEND_EMAIL].setter,
                        )}
                        {renderCreateTicketMonitorAction(
                            inputsAndSetters[MonitorActionType.CREATE_TICKET].inputs, inputsAndSetters[MonitorActionType.CREATE_TICKET].setter,
                        )}
                        {renderSlackMessageMonitorAction(
                            inputsAndSetters[MonitorActionType.SLACK_MESSAGE].inputs, inputsAndSetters[MonitorActionType.SLACK_MESSAGE].setter,
                        )}
                    </div>
                )}
            </div>

            <Box
                className={clsx(globalClasses.stepContentActionsContainer, classes.stepContentActionsContainer)}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
            >
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    onClick={onMonitorActionAnchorElClicked}
                    endIcon={<Icon>arrow_drop_down</Icon>}
                >
                    {capitalizeAll(trans('label.add_monitor_action'))}
                </Button>
                <Menu
                    className={classes.actionTypeMenu}
                    getContentAnchorEl={null}
                    anchorEl={monitorActionMenuAnchorEl}
                    keepMounted
                    anchorOrigin={{
                        vertical  : 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical  : 'top',
                        horizontal: 'right',
                    }}
                    open={isMonitorActionMenuOpened}
                    onClose={onMonitorActionMenuClosed}
                >
                    {monitorActionTypeOptions.map((option: SelectInputOption<MonitorActionType>) => {
                        return (
                            <MenuItem
                                value={option.value}
                                key={option.value}
                                disabled={
                                    (option.value && inputsAndSetters[option.value].inputs !== undefined)
                                    || (option.value === MonitorActionType.CREATE_TICKET && !supportTicketSystems)
                                    || (option.value === MonitorActionType.SLACK_MESSAGE && !hasConfiguredSlackChannels)
                                }
                                onClick={() => {
                                    onMonitorActionMenuClosed();
                                    addMonitorAction(option.value as MonitorActionType);

                                    // Unset "noAction" error if the user added an Action
                                    setInputErrors(produce(inputErrors, (draft: Draft<MonitorActionsInputErrors>) => {
                                        delete draft.noAction;
                                    }));
                                }}
                            >
                                <ListItemIcon>
                                    <FontIcon className={classes.actionTypeMenuIcon} iconName={option.icon || ''} />
                                </ListItemIcon>
                                {option.label}
                            </MenuItem>
                        );
                    })}
                </Menu>
                <div>
                    <Button
                        disabled={!props.canGoBack}
                        onClick={() => {
                            // Validate the inputs AND dispatch onComplete if successful so that we can save the user inputs before going back.
                            // However, if inputs are invalid, we will not dispatch error and just take the user back to the previous step. We will
                            // not save the user's input since it is invalid anyway.
                            validateInputs(true, false);
                            props.onPrevStepClick();
                        }}
                        className={globalClasses.navButton}
                        startIcon={<Icon>navigate_before</Icon>}
                    >
                        {capitalizeAll(trans('label.back'))}
                    </Button>
                    <Button
                        disabled
                        className={globalClasses.navButton}
                        endIcon={<Icon>navigate_next</Icon>}
                    >
                        {capitalizeAll(trans('label.next'))}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                            if (props.onGotoLastStepClick && validateInputs(true)) {
                                props.onGotoLastStepClick();
                            }
                        }}
                        className={clsx(globalClasses.navButton, 'finish')}
                    >
                        {capitalizeAll(trans('label.finish'))}
                    </Button>
                </div>
            </Box>
        </div>
    );
};
